import countBy from 'lodash/countBy';
import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import orderBy from 'lodash/orderBy';
import pickBy from 'lodash/pickBy';
import values from 'lodash/values';
import { createSelector } from 'reselect';

import rollbarLogger from 'config/rollbar';
import {
  makeSelectSamplePointsByAssetTypeIdForDefaultSite,
  selectSamplePoints
} from 'store/modules/samplePoints/selectors';
import { findHeaterHostForSamplePoint } from 'store/modules/samplePoints/utils/find-heater-host';
import { ApplicationRootState } from 'store/types';
import { AssetTypeCode } from 'types/models/asset-type';
import { EventId, Level } from 'types/models/event';
import Notification, {
  EventSamplePoint,
  NotificationStatus,
  SamplePointEvent
} from 'types/models/notification';
import SamplePoint, { MergedSamplePoint, SamplePointId } from 'types/models/samplePoint';
import { TriggerId } from 'types/models/trigger';
import { TriggerType } from 'types/trigger.enum';
import { convertSamplePointToEventSamplePoint } from 'utils/Notification/convert-sample-point-to-event-sample-point';
import { getNotificationSiteId } from 'utils/Notification/get-notification-site';
import { getNotificationTrigger } from 'utils/Notification/get-notification-trigger';
import { getEventSamplePointFromNotification } from 'utils/Notification/sample-point-notification';

import { NotificationsState } from './types';
import { selectDefaultSite } from '../localUserSettings/selectors';

const selectNotificationsState = (state: ApplicationRootState) =>
  state.notifications;

const notificationMapper = (
  notification: Notification,
  safetyCheckSamplePoints: SamplePoint[],
  topLevelSamplePoints: ReturnType<typeof selectSamplePoints>,
  pendingAcknowledgements: ReturnType<typeof selectNotificationsPendingAcknowledgements>
): Notification => {
  const eventSamplePoint: EventSamplePoint | null = getEventSamplePointFromNotification(notification);
  const triggerType: TriggerType | undefined = getNotificationTrigger(notification)?.triggerType;

  const event = { ...notification.event };

  // If event level SOS and there's a matching safety check sample point,
  // replace the notification's sample point with the SOS sample point
  if (eventSamplePoint && event.level === Level.SOS) {
    const matchingSafetyCheck: SamplePoint | undefined = (safetyCheckSamplePoints as MergedSamplePoint[])
      .find((sp) => sp._hidden?.id === eventSamplePoint.id);

    if (matchingSafetyCheck) {
      const samplePointToReplaceWith: EventSamplePoint = convertSamplePointToEventSamplePoint(matchingSafetyCheck);
      (event as SamplePointEvent).samplePoint = samplePointToReplaceWith;
    }
  }

  // If notification comes from a heater sensor, convert to the host sample point for display
  if (eventSamplePoint && (
    triggerType === TriggerType.HEATER_POWER_SUPPLY_ISSUE
    || triggerType === TriggerType.HEATER_POWER_SUPPLY_RECOVERED
    || triggerType === TriggerType.HEATER_WATER_TEMPERATURE_LOW
  )) {
    const matchingHeaterHost: SamplePoint | undefined = findHeaterHostForSamplePoint(
      Object.values(topLevelSamplePoints),
      eventSamplePoint.id
    );
    if (matchingHeaterHost) {
      const samplePointToReplaceWith: EventSamplePoint = convertSamplePointToEventSamplePoint(matchingHeaterHost);
      (event as SamplePointEvent).samplePoint = samplePointToReplaceWith;
    }
  }

  return {
    ...notification,
    status: pendingAcknowledgements[notification.id] ? NotificationStatus.ACKNOWLEDGED : notification.status,
    event
  };
};

const selectNotificationsEnterpriseId = createSelector(
  selectNotificationsState,
  (notificationsState) => notificationsState.enterpriseId as number
);

const selectNotificationsPendingAcknowledgements = createSelector(
  selectNotificationsState,
  (notificationsState) =>
    notificationsState.pendingAcknowledgements || {}
);

/**
 * @deprecated In favor of selectPaginatedNotifications() and selectRecentUnacknowledgedNotifications() for site-wide
 * alert acknowledgement feature
 */
const selectRecentNotifications = createSelector(
  [
    selectNotificationsState,
    makeSelectSamplePointsByAssetTypeIdForDefaultSite(AssetTypeCode.SAFETY_CHECK_IN),
    selectSamplePoints,
    selectNotificationsPendingAcknowledgements
  ],
  (
    notificationsState,
    safetyCheckSamplePoints,
    topLevelSamplePoints,
    pendingAcknowledgements
  ) => {
    try {
      const validNotifications: Notification[] = Object
        .values(notificationsState.data)
        .filter((n: Notification) => !!n.event); // Filter out cached notifications that do not follow our expected type

      const remapStatusAndSamplePoint = (notification: Notification) => notificationMapper(
        notification,
        safetyCheckSamplePoints,
        topLevelSamplePoints,
        pendingAcknowledgements
      );

      const notificationsKeyedById: NotificationsState['data'] = keyBy(
        validNotifications.map(remapStatusAndSamplePoint),
        'id'
      );
      return notificationsKeyedById;
    } catch (error) {
      rollbarLogger.error(
        'Notifications error',
        error as Record<string, unknown>,
        notificationsState.data
      );
      return notificationsState.data;
    }
  }
);

const selectPaginatedNotifications = createSelector(
  [
    selectNotificationsState,
    makeSelectSamplePointsByAssetTypeIdForDefaultSite(AssetTypeCode.SAFETY_CHECK_IN),
    selectSamplePoints,
    selectNotificationsPendingAcknowledgements
  ],
  (
    notificationsState,
    safetyCheckSamplePoints,
    topLevelSamplePoints,
    pendingAcknowledgements
  ) => {
    const mapNotification = (notification: Notification) => notificationMapper(
      notification,
      safetyCheckSamplePoints,
      topLevelSamplePoints,
      pendingAcknowledgements
    );

    const notifications = orderBy(notificationsState.paginatedData, 'event.date', 'desc')
      .map(mapNotification);

    return {
      data: notifications,
      total: notificationsState.paginatedDataTotal
    };
  }
);

const selectRecentUnacknowledgedNotifications = createSelector(
  [
    selectNotificationsState,
    makeSelectSamplePointsByAssetTypeIdForDefaultSite(AssetTypeCode.SAFETY_CHECK_IN),
    selectSamplePoints,
    selectNotificationsPendingAcknowledgements
  ],
  (
    notificationsState,
    safetyCheckSamplePoints,
    topLevelSamplePoints,
    pendingAcknowledgements
  ) => {
    // notificationsState.data will be empty when site-wide alert acknowledgement feature is enabled
    const notifications = values(notificationsState.data).length >= 1
      ? values(notificationsState.data)
      : values(notificationsState.recentUnacknowledgedData);

    const mapNotification = (notification: Notification) => notificationMapper(
      notification,
      safetyCheckSamplePoints,
      topLevelSamplePoints,
      pendingAcknowledgements
    );

    return notifications
      // TODO: Remove this filter call once we no longer rely on `notificationsState.data`
      // Filter out cached notifications that do not follow our expected type
      .filter((n: Notification) => !!n.event)
      .map(mapNotification)
      .filter((n) => n.status === NotificationStatus.SENT);
  }
);

const makeSelectNotificationByEventId = (eventId: EventId) => createSelector(
  [
    selectRecentNotifications,
    selectRecentUnacknowledgedNotifications,
    selectPaginatedNotifications
  ],
  (recentNotifications, recentUnacknowledgedNotifications, paginatedNotifications) => {
    const notifications = Object.values(recentNotifications).length >= 1
      ? Object.values(recentNotifications)
      : [...Object.values(recentUnacknowledgedNotifications), ...Object.values(paginatedNotifications.data)];
    return notifications.find((n) => n.event.id === eventId);
  }
);

const selectRecentUnacknowledgedNotificationsCount = createSelector(
  selectRecentUnacknowledgedNotifications,
  (unacknowledgedNotifications) => countBy(
    unacknowledgedNotifications,
    (n) => getNotificationSiteId(n)
  )
);

const selectRecentUnacknowledgedNotificationsGroupedByEventSamplePointId = createSelector(
  selectRecentUnacknowledgedNotifications,
  (recentUnacknowledgedNotifications) => groupBy(
    recentUnacknowledgedNotifications,
    'event.samplePoint.id'
  )
);

// ==============================
// ARRAY UNORDERED
// ==============================
/**
 * @deprecated In favor of selectPaginatedNotifications() for site-wide alert acknowledgement feature
 */
const selectRecentNotificationsForDefaultSite = createSelector(
  [selectRecentNotifications, selectDefaultSite],
  (recentNotificationsDict, defaultSite) => Object.values(
    pickBy(
      recentNotificationsDict,
      (notification) => getNotificationSiteId(notification) === defaultSite?.id
    )
  )
);

const selectRecentUnacknowledgedNotificationsForDefaultSite = createSelector(
  [selectRecentUnacknowledgedNotifications, selectDefaultSite],
  (unacknowledgedNotifications, defaultSite) => unacknowledgedNotifications.filter(n =>
    getNotificationSiteId(n) === defaultSite?.id
  )
);

// ==============================
// ARRAY ORDERED BY EVENT.DATE, LATEST FIRST
// ==============================
/**
 * @deprecated In favor of selectPaginatedNotifications() for site-wide alert acknowledgement feature
 */
const selectRecentNotificationsForDefaultSiteOrderedByEventDate = createSelector(
  selectRecentNotificationsForDefaultSite,
  (recentNotificationsForDefaultSite) => orderBy(recentNotificationsForDefaultSite, 'event.date', 'desc')
);

const selectRecentUnacknowledgedNotificationsForDefaultSiteOrderedByEventDate = createSelector(
  selectRecentUnacknowledgedNotificationsForDefaultSite,
  (unacknowledgedNotificationsForDefaultSite) => orderBy(unacknowledgedNotificationsForDefaultSite, 'event.date', 'desc')
);

/** Ordered by event.date latest first */
const makeSelectRecentUnacknowledgedNotificationsByEventSamplePointId = (
  samplePointId: SamplePointId
) =>
  createSelector(
    selectRecentUnacknowledgedNotificationsGroupedByEventSamplePointId,
    (groupedBySamplePointId) => orderBy(groupedBySamplePointId[samplePointId] || [], 'event.date', 'desc')
  );

/** Ordered by severity (highest first), then event.date latest first */
const makeSelectRecentUnacknowledgedNotificationsByWeatherStationController = (
  associatedSamplePointIds: SamplePointId[]
) => createSelector(
  selectRecentUnacknowledgedNotificationsGroupedByEventSamplePointId,
  (groupedBySamplePointId) => {
    const associatedSPNotifications: Notification[] = associatedSamplePointIds.map((samplePointId) =>
      groupedBySamplePointId[samplePointId] || []
    ).flat();
    return orderBy(associatedSPNotifications, ['event.level', 'event.date'], ['desc', 'desc']);
  }
);

/** Ordered by event.date latest first */
const makeSelectRecentUnacknowledgedNotificationsByTriggerIds = (
  triggerIds: TriggerId[]
) => createSelector(
  selectRecentUnacknowledgedNotifications,
  (unacknowledgedNotifications) => {
    const filteredNotifications = unacknowledgedNotifications.filter(notification => {
      const eventTriggerId = (notification.event as SamplePointEvent).triggerId;
      if (!eventTriggerId) return false;
      return triggerIds.includes(eventTriggerId);
    });
    return orderBy(filteredNotifications, 'event.date', 'desc');
  }
);

export {
  makeSelectNotificationByEventId,
  makeSelectRecentUnacknowledgedNotificationsByEventSamplePointId,
  makeSelectRecentUnacknowledgedNotificationsByTriggerIds,
  makeSelectRecentUnacknowledgedNotificationsByWeatherStationController,
  selectPaginatedNotifications,
  selectRecentNotifications,
  selectNotificationsEnterpriseId,
  selectRecentNotificationsForDefaultSiteOrderedByEventDate,
  selectNotificationsPendingAcknowledgements,
  selectNotificationsState,
  selectRecentUnacknowledgedNotifications,
  selectRecentUnacknowledgedNotificationsCount,
  selectRecentUnacknowledgedNotificationsForDefaultSiteOrderedByEventDate
};
